import React, { useState, useRef, useCallback, useEffect } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from './ui/table';
import { Card } from './ui/card';
import { Input } from './ui/input';
import { Button } from './ui/button';
import { Checkbox } from './ui/checkbox';
import { Plus, Loader2, Trash2, X } from 'lucide-react';
import { format } from 'date-fns';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from './ui/dialog';
import { Label } from './ui/label';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from './ui/select';
import { useToast } from './ui/use-toast';
import { useIsAndroid } from '../hooks/use-android';
import { useBackNavigation } from '../hooks/use-back-navigation';
import { useSettings } from '../hooks/useSettings';
import { getBaseUrl } from '@/lib/api';

interface SupplierPayment {
  ID: number;
  PaidOn: string;
  SupplierID: number;
  PaidAmount: number;
  Reference: string;
  BankAccountID: number;
  PaymentType: number;
  CompanyID: number;
  ExpensePayment: number;
  CreatedBy: string;
  PaymentTypeText: string;
  InvoiceID?: number;
}

interface SupplierInvoice {
  InvDateGen: string;
  InvRefNo: string;
  GRNNo: string;
  PayableAmount: number;
  ID?: number;
  DueBalAmount?: number;
}

interface SupplierPaymentsProps {
  token: string;
  supplierId: number;
  selectedInvoices: Set<string>;
  invoices: SupplierInvoice[];
  onBack?: () => void;
}

// Payment types mapping
const PAYMENT_TYPES = [
  { value: 1, label: 'Cash' },
  { value: 2, label: 'Cheque' },
  { value: 3, label: 'Credit Card' },
  { value: 4, label: 'Debit Card' },
  { value: 5, label: 'Bank Transfer' },
  { value: 6, label: 'UPI' },
  { value: 7, label: 'Wallet' },
];

function formatCurrency(val?: number) {
  if (typeof val !== 'number') return '₹0.00';
  return `₹${val.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

function formatDateTime(dateStr?: string) {
  if (!dateStr) return '-';
  try {
    return format(new Date(dateStr), 'dd-MM-yyyy');
  } catch {
    return dateStr;
  }
}

const SupplierPayments: React.FC<SupplierPaymentsProps> = ({ token, supplierId, selectedInvoices, invoices, onBack }) => {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const isAndroid = useIsAndroid();
  const { primaryCompanyId } = useSettings();
  const [isAddPaymentOpen, setIsAddPaymentOpen] = useState(false);
  const [isDeleteConfirmOpen, setIsDeleteConfirmOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [deletingPaymentId, setDeletingPaymentId] = useState<number | null>(null);
  const [deletingInvoiceId, setDeletingInvoiceId] = useState<number | null>(null);
  const [invoiceOptions, setInvoiceOptions] = useState<Array<{ InvRefNo: string; ID: string; companyid?: number }>>([]);
  const [invoiceLoading, setInvoiceLoading] = useState(false);

  // Android back: close form/modal first, else call parent (switch to invoices tab)
  useBackNavigation({
    onBack: () => {
      if (isAddPaymentOpen) {
        setIsAddPaymentOpen(false);
        return;
      }
      if (isDeleteConfirmOpen) {
        setIsDeleteConfirmOpen(false);
        setDeletingPaymentId(null);
        setDeletingInvoiceId(null);
        return;
      }
      onBack?.();
    },
    canGoBack: true,
    showExitConfirmation: false,
  });
  
  // Lazy loading state for mobile cards
  const [mobileLoadedCount, setMobileLoadedCount] = useState(20);
  const [isMobileLoading, setIsMobileLoading] = useState(false);
  const mobileListRef = useRef<HTMLDivElement>(null);
  const bankFieldsRef = useRef<HTMLDivElement>(null);
  const dialogContentRef = useRef<HTMLDivElement>(null);
  const [paymentForm, setPaymentForm] = useState({
    paidAmount: '',
    reference: '',
    paidOn: format(new Date(), 'yyyy-MM-dd'),
    paymentType: '1', // Default to Cash
    companyId: '',
    locationId: '',
    bankId: '',
    bankAccountNo: '',
    invoiceId: '',
    linkInvoice: false,
    invoiceSearch: '',
    selectedInvoiceId: '',
    selectedInvoiceCompanyId: '',
  });

  const baseUrl = getBaseUrl();
  const authToken = token.startsWith('Bearer ') ? token : `Bearer ${token}`;

  // Fetch companies for dropdown
  const { data: companiesData, isLoading: isCompaniesLoading } = useQuery({
    queryKey: ['companies', baseUrl],
    queryFn: async () => {
      const response = await fetch(`${baseUrl}/api/Companies`, {
        headers: {
          Authorization: authToken,
          'Content-Type': 'application/json',
        },
      });
      if (!response.ok) throw new Error('Failed to fetch companies');
      const jsonData = await response.json();
      console.log('=== Companies API Response ===');
      console.log(JSON.stringify(jsonData, null, 2));
      return jsonData;
    },
    enabled: isAddPaymentOpen,
    staleTime: 1000 * 60 * 5,
  });

  // Fetch locations for dropdown
  const { data: locationsData, isLoading: isLocationsLoading } = useQuery({
    queryKey: ['locations', baseUrl],
    queryFn: async () => {
      const response = await fetch(`${baseUrl}/api/Locations`, {
        headers: {
          Authorization: authToken,
          'Content-Type': 'application/json',
        },
      });
      if (!response.ok) throw new Error('Failed to fetch locations');
      const jsonData = await response.json();
      console.log('=== Locations API Response ===');
      console.log(JSON.stringify(jsonData, null, 2));
      return jsonData;
    },
    enabled: isAddPaymentOpen,
    staleTime: 1000 * 60 * 5,
  });

  // Fetch banks for dropdown (when Bank Transfer is selected)
  const { data: banksData, isLoading: isBanksLoading } = useQuery({
    queryKey: ['banks', baseUrl],
    queryFn: async () => {
      const response = await fetch(`${baseUrl}/api/GetBank`, {
        headers: {
          Authorization: authToken,
          'Content-Type': 'application/json',
        },
      });
      if (!response.ok) throw new Error('Failed to fetch banks');
      const jsonData = await response.json();
      console.log('=== Banks API Response (api/GetBank) ===');
      console.log(JSON.stringify(jsonData, null, 2));
      console.log('=== End of Banks API Response ===');
      
      // Log data extraction
      const banksArray = Array.isArray(jsonData?.data) 
        ? jsonData.data 
        : Array.isArray(jsonData) 
        ? jsonData 
        : [];
      console.log('=== Banks Data Extraction ===');
      console.log('├─ Response Type:', typeof jsonData);
      console.log('├─ Has "data" property:', jsonData?.data ? 'YES ✓' : 'NO');
      console.log('├─ Banks extracted:', banksArray.length);
      console.log('└─ Banks:', banksArray);
      console.log('=== End of Data Extraction ===');
      
      return jsonData;
    },
    enabled: isAddPaymentOpen && paymentForm.paymentType === '5',
    staleTime: 1000 * 60 * 5,
  });

  const companies = Array.isArray(companiesData?.data) 
    ? companiesData.data 
    : Array.isArray(companiesData) 
    ? companiesData 
    : [];

  const locations = Array.isArray(locationsData?.data) 
    ? locationsData.data 
    : Array.isArray(locationsData) 
    ? locationsData 
    : [];

  // Banks array - extract from response (handles both wrapped and direct array)
  const banks = Array.isArray(banksData?.data) 
    ? banksData.data 
    : Array.isArray(banksData) 
    ? banksData 
    : [];

  // Set default company, location, and invoice when data is loaded
  React.useEffect(() => {
    if (companies.length > 0 && !paymentForm.companyId && isAddPaymentOpen) {
      const defaultCompany = primaryCompanyId
        ? companies.find((c: any) => String(c.Id) === primaryCompanyId || String(c.CompanyID) === primaryCompanyId || String(c.id) === primaryCompanyId)
        : null;
      const companyToUse = defaultCompany || companies[0];
      const companyId = String(companyToUse.Id || companyToUse.CompanyID || companyToUse.id);
      setPaymentForm(prev => ({ ...prev, companyId }));
    }
  }, [companies, paymentForm.companyId, isAddPaymentOpen, primaryCompanyId]);

  // Set default location when data is loaded
  React.useEffect(() => {
    if (locations.length > 0 && !paymentForm.locationId && isAddPaymentOpen) {
      const firstLocation = locations[0];
      const locationId = String(firstLocation.ID || firstLocation.LocationID || firstLocation.LocationId);
      setPaymentForm(prev => ({ ...prev, locationId }));
    }
  }, [locations, paymentForm.locationId, isAddPaymentOpen]);

  // Fetch supplier due invoices for linking
  const fetchSupplierDueInvoices = useCallback(async (supplierCode: string) => {
    if (!supplierCode || invoiceLoading) return;

    const endpoint = `${baseUrl}/api/SupplierDueInvoiceNumbers?Suppliercode=${encodeURIComponent(supplierCode)}`;
    console.log('🛰️ SupplierDueInvoiceNumbers request:', endpoint);
    setInvoiceLoading(true);
    setInvoiceOptions([]);

    try {
      const response = await fetch(endpoint, {
        headers: {
          Authorization: authToken,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        console.error('SupplierDueInvoiceNumbers error status:', response.status, response.statusText);
        throw new Error('Failed to fetch supplier invoices');
      }

      const json = await response.json();
      console.log('🛰️ SupplierDueInvoiceNumbers response:', json);

      if (json.status === 0 && Array.isArray(json.data)) {
        const opts: Array<{ InvRefNo: string; ID: string; companyid?: number }> =
          json.data
            .map((item: any) => {
              const InvRefNo = item.InvRefNo != null ? String(item.InvRefNo) : '';
              const ID = item.ID != null ? String(item.ID) : '';
              const companyid = item.companyid != null ? Number(item.companyid) : undefined;
              return InvRefNo && ID
                ? {
                    InvRefNo,
                    ID,
                    companyid,
                  }
                : null;
            })
            .filter(Boolean) as Array<{
              InvRefNo: string;
              ID: string;
              companyid?: number;
            }>;

        setInvoiceOptions(opts);
      } else {
        toast({
          variant: 'destructive',
          description: json.message || 'Unable to load invoices for this supplier',
        });
      }
    } catch (error) {
      console.error('SupplierDueInvoiceNumbers fetch failed:', error);
      toast({
        variant: 'destructive',
        description: 'Unable to load invoices for this supplier',
      });
      setInvoiceOptions([]);
    } finally {
      setInvoiceLoading(false);
    }
  }, [invoiceLoading, authToken, baseUrl, toast]);

  // Set default invoice when modal opens
  React.useEffect(() => {
    if (isAddPaymentOpen && !paymentForm.invoiceId) {
      // If one invoice is selected, use it
      if (selectedInvoices.size === 1) {
        const selectedInvoiceRef = Array.from(selectedInvoices)[0];
        const selectedInvoice = invoices.find(inv => inv.InvRefNo === selectedInvoiceRef);
        if (selectedInvoice?.ID) {
          setPaymentForm(prev => ({ ...prev, invoiceId: String(selectedInvoice.ID) }));
        }
      } else if (invoices.length > 0) {
        // Otherwise use first invoice
        const firstInvoice = invoices[0];
        if (firstInvoice?.ID) {
          setPaymentForm(prev => ({ ...prev, invoiceId: String(firstInvoice.ID) }));
        }
      }
    }
  }, [isAddPaymentOpen, paymentForm.invoiceId, selectedInvoices, invoices]);

  // Set default bank and account when banks are loaded
  React.useEffect(() => {
    if (banks.length > 0 && isAddPaymentOpen && paymentForm.paymentType === '5') {
      // Set default bank if not set
      if (!paymentForm.bankId) {
        const firstBank = banks[0];
        setPaymentForm(prev => ({ 
          ...prev, 
          bankId: String(firstBank.Id),
        }));
      }
      // Set default account if not set
      if (!paymentForm.bankAccountNo) {
        const firstBank = banks[0];
        setPaymentForm(prev => ({ 
          ...prev, 
          bankAccountNo: firstBank.AccountNo || '',
        }));
      }
    }
  }, [banks, paymentForm.bankId, paymentForm.bankAccountNo, paymentForm.paymentType, isAddPaymentOpen]);

  // Handle bank name change - update account number to match
  const handleBankChange = (bankId: string) => {
    // Find the bank and get its account number
    const selectedBank = banks.find((bank: any) => String(bank.Id) === bankId);
    if (selectedBank) {
      setPaymentForm(prev => ({
        ...prev,
        bankId: bankId,
        bankAccountNo: selectedBank.AccountNo || '', // Update account number to match the bank
      }));
    } else {
      setPaymentForm(prev => ({
        ...prev,
        bankId: bankId,
      }));
    }
  };

  // Handle account number change - update bank name to match
  const handleAccountNoChange = (accountNo: string) => {
    // Find the bank that has this account number
    const bankWithAccount = banks.find((bank: any) => bank.AccountNo === accountNo);
    if (bankWithAccount) {
      setPaymentForm(prev => ({
        ...prev,
        bankAccountNo: accountNo,
        bankId: String(bankWithAccount.Id), // Update bank to match the account
      }));
    } else {
      setPaymentForm(prev => ({
        ...prev,
        bankAccountNo: accountNo,
      }));
    }
  };

  // Debug: Log component mount and props
  React.useEffect(() => {
    console.log('\n🔷 ═══════════════════════════════════════════════════════════');
    console.log('🔷 SUPPLIER PAYMENTS COMPONENT MOUNTED');
    console.log('🔷 ═══════════════════════════════════════════════════════════');
    console.log('📌 Props received:');
    console.log('   ├─ Supplier ID:', supplierId);
    console.log('   ├─ Token present:', !!token);
    console.log('   ├─ Token length:', token?.length || 0);
    console.log('   └─ Base URL:', baseUrl);
    console.log('🔷 ═══════════════════════════════════════════════════════════\n');
  }, [supplierId, token, baseUrl]);

  // Fetch payments data
  const { data, isLoading, error } = useQuery({
    queryKey: ['supplier-payments', supplierId, baseUrl],
    queryFn: async () => {
      console.log('\n╔═══════════════════════════════════════════════════════════════╗');
      console.log('║          FETCHING SUPPLIER PAYMENTS - GET REQUEST             ║');
      console.log('╚═══════════════════════════════════════════════════════════════╝\n');
      
      console.log('📋 REQUEST DETAILS:');
      console.log('├─ Method: GET');
      console.log('├─ Endpoint: /api/GetSupplierPayments');
      console.log(`├─ Supplier ID: ${supplierId}`);
      console.log(`├─ Base URL: ${baseUrl}`);
      console.log(`└─ Full URL: ${baseUrl}/api/GetSupplierPayments?SupplierID=${supplierId}`);
      
      console.log('\n🔐 HEADERS:');
      console.log('├─ Authorization:', authToken.substring(0, 30) + '...');
      console.log('└─ Content-Type: application/json');
      
      console.log('\n⏳ Sending request...\n');

      const response = await fetch(
        `${baseUrl}/api/GetSupplierPayments?SupplierID=${supplierId}`,
        {
          headers: {
            Authorization: authToken,
            'Content-Type': 'application/json',
          },
        }
      );

      console.log('✅ RESPONSE RECEIVED:');
      console.log('├─ Status:', response.status, response.statusText);
      console.log('├─ Status OK:', response.ok);
      console.log('└─ Headers:', JSON.stringify(Object.fromEntries(response.headers.entries()), null, 2));

      if (!response.ok) {
        const errorText = await response.text();
        console.log('\n❌ ERROR RESPONSE:');
        console.log(errorText);
        console.log('\n═══════════════════════════════════════════════════════════════\n');
        throw new Error('Failed to fetch supplier payments');
      }
      
      const jsonData = await response.json();

      console.log('\n📦 RESPONSE BODY (JSON):');
      console.log('┌─────────────────────────────────────────────────────────────┐');
      console.log(JSON.stringify(jsonData, null, 2));
      console.log('└─────────────────────────────────────────────────────────────┘');
      
      console.log('\n📊 DATA SUMMARY:');
      console.log(`├─ Response Type: ${typeof jsonData}`);
      console.log(`├─ Has 'data' property: ${jsonData?.data ? 'YES ✓' : 'NO'}`);
      
      // Check if data is in jsonData.data (API wrapper format)
      const paymentsArray = Array.isArray(jsonData?.data) ? jsonData.data : Array.isArray(jsonData) ? jsonData : [];
      
      if (paymentsArray.length > 0) {
        console.log(`├─ Payments Array Location: ${Array.isArray(jsonData?.data) ? 'jsonData.data' : 'jsonData'}`);
        console.log(`├─ Payments Count: ${paymentsArray.length} payment(s)`);
        console.log(`└─ Status: Data found ✅`);
        console.log('\n💰 SAMPLE PAYMENT:');
        console.log(JSON.stringify(paymentsArray[0], null, 2));
      } else {
        console.log(`├─ Payments Count: 0`);
        console.log(`└─ Status: No payments found ⚠️`);
      }
      
      console.log('\n═══════════════════════════════════════════════════════════════\n');

      return jsonData;
    },
    enabled: !!supplierId,
    staleTime: 0,
    refetchOnMount: true,
    refetchOnWindowFocus: false,
  });

  // Extract payments array from API response
  // API returns: { status, message, data: [...] }
  const payments: SupplierPayment[] = Array.isArray(data?.data) 
    ? data.data 
    : Array.isArray(data) 
    ? data 
    : [];

  // Debug: Log query state changes
  React.useEffect(() => {
    console.log('\n📊 ═══════════════════════════════════════════════════════════');
    console.log('📊 QUERY STATE UPDATE & DATA PROCESSING');
    console.log('📊 ═══════════════════════════════════════════════════════════');
    console.log('📌 Current State:');
    console.log('   ├─ Loading:', isLoading);
    console.log('   ├─ Error:', error ? 'YES ❌' : 'NO ✓');
    if (error) {
      console.log('   │  └─ Error Details:', error);
    }
    console.log('   ├─ Data Received:', data ? 'YES ✓' : 'NO');
    console.log('   ├─ Data Structure:', data ? (data.data ? 'Wrapped (data.data)' : 'Direct array') : 'N/A');
    console.log('   ├─ Payments Extracted:', payments.length);
    if (payments.length > 0) {
      console.log('   ├─ UI Will Show: Table/Cards with data ✅');
      console.log('   └─ Sample Payment:');
      console.log('      ', JSON.stringify(payments[0], null, 6));
    } else {
      console.log('   ├─ UI Will Show: "No payments found" message');
      console.log('   └─ Reason:', data ? 'Empty data.data array' : 'No data received');
    }
    console.log('📊 ═══════════════════════════════════════════════════════════\n');
  }, [isLoading, error, data, payments, supplierId]);

  // Auto-scroll to bank fields when Bank Transfer is selected
  useEffect(() => {
    if (paymentForm.paymentType === '5' && bankFieldsRef.current && dialogContentRef.current) {
      // Small delay to ensure DOM is updated
      setTimeout(() => {
        if (bankFieldsRef.current && dialogContentRef.current) {
          const bankFieldsTop = bankFieldsRef.current.offsetTop;
          const scrollContainer = dialogContentRef.current;
          scrollContainer.scrollTo({
            top: bankFieldsTop - 20, // 20px offset from top
            behavior: 'smooth'
          });
        }
      }, 100);
    }
  }, [paymentForm.paymentType]);

  const resetPaymentForm = () => {
    setPaymentForm({
      paidAmount: '',
      reference: '',
      paidOn: format(new Date(), 'yyyy-MM-dd'),
      paymentType: '1', // Default to Cash
      companyId: '',
      locationId: '',
      bankId: '',
      bankAccountNo: '',
      invoiceId: '',
      linkInvoice: false,
      invoiceSearch: '',
      selectedInvoiceId: '',
      selectedInvoiceCompanyId: '',
    });
  };

  const handleOpenAddPayment = () => {
    resetPaymentForm();
    setIsAddPaymentOpen(true);
    
    // Fetch supplier due invoices when opening payment modal
    // We need to get supplier code - for now, use supplierId as code
    // In a real implementation, you might need to pass supplierCode as a prop
    fetchSupplierDueInvoices(String(supplierId));
  };

  const handleCloseAddPayment = () => {
    resetPaymentForm();
    setIsAddPaymentOpen(false);
  };

  // Get payment type text for API
  const getPaymentTypeText = (typeValue: string): string => {
    const typeMap: { [key: string]: string } = {
      '1': 'cash',
      '2': 'cheque',
      '3': 'card',
      '5': 'bank transfer',
      '7': 'wallet',
    };
    return typeMap[typeValue] || 'cash';
  };

  const handleSavePayment = async () => {
    // Validate required fields
    const missingFields = [];
    if (!paymentForm.paidAmount || parseFloat(paymentForm.paidAmount) <= 0) {
      missingFields.push('Paid Amount');
    }
    if (!paymentForm.paidOn) missingFields.push('Paid On Date');
    if (!paymentForm.paymentType) missingFields.push('Payment Type');
    if (!paymentForm.companyId) missingFields.push('Company');
    if (!paymentForm.locationId) missingFields.push('Location');
    
    // If linking to invoice, validate invoice selection
    if (paymentForm.linkInvoice) {
      if (!paymentForm.selectedInvoiceId) {
        missingFields.push('Select an invoice to link');
      }
    }
    
    // If Bank Transfer is selected, validate bank
    if (paymentForm.paymentType === '5') {
      if (!paymentForm.bankId) missingFields.push('Bank Name');
      if (!paymentForm.bankAccountNo) missingFields.push('A/c No');
    }

    if (missingFields.length > 0) {
      toast({
        variant: 'destructive',
        title: 'Missing Required Fields',
        description: `Please fill in the following fields: ${missingFields.join(', ')}`,
      });
      return;
    }

    try {
      setIsSubmitting(true);

      // Build payment detail object
      const paymentDetail: any = {
        PaidAmount: parseFloat(paymentForm.paidAmount),
        PaidOn: paymentForm.paidOn,
        Reference: paymentForm.reference.trim(),
        PaymentType: getPaymentTypeText(paymentForm.paymentType),
        ExpensePayment: 0,
      };

      // Add BankAccountID only if Bank Transfer is selected
      if (paymentForm.paymentType === '5' && paymentForm.bankId) {
        paymentDetail.BankAccountID = parseInt(paymentForm.bankId);
      }

      // Build the complete payload
      const payload = {
        Payments: {
          SupplierID: supplierId,
          CompanyID: parseInt(paymentForm.companyId),
          LocationId: parseInt(paymentForm.locationId),
          SupplierInvoiceId: paymentForm.linkInvoice && paymentForm.selectedInvoiceId 
            ? parseFloat(paymentForm.selectedInvoiceId) 
            : "",
          PaymentDetails: [paymentDetail],
        },
      };

      console.log('\n╔═══════════════════════════════════════════════════════════════╗');
      console.log('║          POST api/PaySupplierPayments - REQUEST              ║');
      console.log('╚═══════════════════════════════════════════════════════════════╝\n');
      console.log('📋 API DETAILS:');
      console.log('├─ Method: POST');
      console.log('├─ Endpoint: /api/PaySupplierPayments');
      console.log(`├─ Base URL: ${baseUrl}`);
      console.log(`└─ Full URL: ${baseUrl}/api/PaySupplierPayments`);
      console.log('\n🔐 HEADERS:');
      console.log('├─ Authorization:', authToken.substring(0, 30) + '...');
      console.log('└─ Content-Type: application/json');
      console.log('\n📦 REQUEST BODY (JSON):');
      console.log('┌─────────────────────────────────────────────────────────────┐');
      console.log(JSON.stringify(payload, null, 2));
      console.log('└─────────────────────────────────────────────────────────────┘');
      console.log('\n⏳ Sending request...\n');

      const response = await fetch(`${baseUrl}/api/PaySupplierPayments`, {
        method: 'POST',
        headers: {
          Authorization: authToken,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      });

      console.log('✅ RESPONSE RECEIVED:');
      console.log('├─ Status:', response.status, response.statusText);
      console.log('├─ Status OK:', response.ok);
      console.log('└─ Headers:', JSON.stringify(Object.fromEntries(response.headers.entries()), null, 2));

      if (!response.ok) {
        const errorText = await response.text();
        console.log('\n❌ ERROR RESPONSE:');
        console.log(errorText);
        console.log('\n═══════════════════════════════════════════════════════════════\n');
        throw new Error(errorText || 'Failed to add supplier payment');
      }

      const responseData = await response.json();
      console.log('\n📦 RESPONSE BODY (JSON):');
      console.log('┌─────────────────────────────────────────────────────────────┐');
      console.log(JSON.stringify(responseData, null, 2));
      console.log('└─────────────────────────────────────────────────────────────┘');
      console.log('\n═══════════════════════════════════════════════════════════════\n');

      // Check status: 0 = success, -1 = error
      if (responseData.status === -1) {
        toast({
          variant: 'destructive',
          title: 'Payment Failed',
          description: responseData.message || 'Failed to add payment.',
        });
        return;
      }

      // Status 0 = success
      if (responseData.status === 0) {
        toast({
          title: 'Payment Added Successfully',
          description: responseData.message || 'The payment has been saved successfully.',
        });
      } else {
        // Unexpected status
        toast({
          title: 'Payment Added',
          description: 'The payment has been saved.',
        });
      }

      await queryClient.invalidateQueries({ queryKey: ['supplier-payments', supplierId] });
      await queryClient.invalidateQueries({ queryKey: ['supplier-balance'] });
      handleCloseAddPayment();
    } catch (err) {
      console.error('❌ Error adding payment:', err);
      toast({
        variant: 'destructive',
        title: 'Save Failed',
        description: err instanceof Error ? err.message : 'An unexpected error occurred while saving the payment.',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleOpenDeleteConfirm = (paymentId: number) => {
    console.log('\n🔘 ═══════════════════════════════════════════════════════════');
    console.log('🔘 DELETE PAYMENT - GETTING INVOICE ID FROM CHECKBOX');
    console.log('🔘 ═══════════════════════════════════════════════════════════');
    console.log('📌 Payment ID (from clicked delete button):', paymentId);
    console.log('📌 Selected Invoices (checkboxes):', Array.from(selectedInvoices));
    console.log('📌 Available Invoices:', invoices.map(inv => ({ ID: inv.ID, InvRefNo: inv.InvRefNo })));
    
    setDeletingPaymentId(paymentId);
    
    // Get invoice ID from selected invoices (checkboxes)
    // selectedInvoices contains invoice reference numbers (InvRefNo)
    let invoiceId: number | null = null;
    
    if (selectedInvoices.size > 0) {
      // Get the first selected invoice reference
      const selectedInvoiceRef = Array.from(selectedInvoices)[0];
      console.log('📌 Selected Invoice Reference:', selectedInvoiceRef);
      
      // Find the invoice in the invoices array by reference number
      const selectedInvoice = invoices.find(inv => inv.InvRefNo === selectedInvoiceRef);
      console.log('📌 Found Invoice:', selectedInvoice ? { ID: selectedInvoice.ID, InvRefNo: selectedInvoice.InvRefNo } : 'NOT FOUND');
      
      if (selectedInvoice?.ID) {
        invoiceId = selectedInvoice.ID;
        console.log('✅ Invoice ID found from checkbox:', invoiceId);
      } else {
        console.log('⚠️ Invoice not found by reference number, trying fallback...');
      }
    } else {
      console.log('⚠️ No invoice checkbox selected');
    }
    
    // Fallback: if no invoice is selected, use the first invoice
    if (!invoiceId && invoices.length > 0) {
      invoiceId = invoices[0]?.ID || null;
      console.log('📌 Using fallback invoice ID (first invoice):', invoiceId);
    }
    
    console.log('📌 Final Invoice ID to use:', invoiceId);
    console.log('🔘 ═══════════════════════════════════════════════════════════\n');
    
    setDeletingInvoiceId(invoiceId);
    setIsDeleteConfirmOpen(true);
  };

  const handleCloseDeleteConfirm = () => {
    setDeletingPaymentId(null);
    setDeletingInvoiceId(null);
    setIsDeleteConfirmOpen(false);
  };

  const handleDeletePayment = async () => {
    if (!deletingPaymentId) {
      toast({
        variant: 'destructive',
        title: 'No Payment Selected',
        description: 'Please select a payment to delete.',
      });
      return;
    }

    if (!deletingInvoiceId) {
      toast({
        variant: 'destructive',
        title: 'Invoice ID Missing',
        description: 'Unable to determine invoice ID for this payment.',
      });
      return;
    }

    try {
      setIsDeleting(true);

      // Verify we have both IDs
      console.log('\n🗑️ ═══════════════════════════════════════════════════════════');
      console.log('🗑️ DELETE PAYMENT - VERIFYING IDs');
      console.log('🗑️ ═══════════════════════════════════════════════════════════');
      console.log('📌 Payment ID (from delete button):', deletingPaymentId);
      console.log('📌 Invoice ID (from checkbox):', deletingInvoiceId);
      console.log('🗑️ ═══════════════════════════════════════════════════════════\n');

      const payload = {
        paymentId: deletingPaymentId,
        invoiceId: deletingInvoiceId,
      };

      console.log('\n╔═══════════════════════════════════════════════════════════════╗');
      console.log('║       DELETE api/DeleteSupplierPayment - REQUEST              ║');
      console.log('╚═══════════════════════════════════════════════════════════════╝\n');
      
      console.log('📋 API DETAILS:');
      console.log('├─ Method: DELETE');
      console.log('├─ Endpoint: /api/DeleteSupplierPayment');
      console.log(`├─ Base URL: ${baseUrl}`);
      console.log(`└─ Full URL: ${baseUrl}/api/DeleteSupplierPayment`);
      
      console.log('\n🔐 HEADERS:');
      console.log('├─ Authorization:', authToken.substring(0, 30) + '...');
      console.log('└─ Content-Type: application/json');
      
      console.log('\n📦 REQUEST BODY (JSON):');
      console.log('┌─────────────────────────────────────────────────────────────┐');
      console.log(JSON.stringify(payload, null, 2));
      console.log('└─────────────────────────────────────────────────────────────┘');
      
      console.log('\n⏳ Sending request...\n');

      const response = await fetch(`${baseUrl}/api/DeleteSupplierPayment`, {
        method: 'DELETE',
        headers: {
          Authorization: authToken,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      });

      console.log('✅ RESPONSE RECEIVED:');
      console.log('├─ Status:', response.status, response.statusText);
      console.log('├─ Status OK:', response.ok);
      console.log('└─ Headers:', JSON.stringify(Object.fromEntries(response.headers.entries()), null, 2));

      if (!response.ok) {
        const errorText = await response.text();
        console.log('\n❌ ERROR RESPONSE:');
        console.log('┌─────────────────────────────────────────────────────────────┐');
        console.log(errorText);
        console.log('└─────────────────────────────────────────────────────────────┘');
        console.log('\n═══════════════════════════════════════════════════════════════\n');
        throw new Error(errorText || 'Failed to delete payment');
      }

      const responseData = await response.json();
      console.log('\n📦 RESPONSE BODY (JSON):');
      console.log('┌─────────────────────────────────────────────────────────────┐');
      console.log(JSON.stringify(responseData, null, 2));
      console.log('└─────────────────────────────────────────────────────────────┘');
      console.log('\n═══════════════════════════════════════════════════════════════\n');

      // Check if API returned an error status (status = -1 means failure)
      if (responseData.status === -1) {
        toast({
          variant: 'destructive',
          title: 'Delete Failed',
          description: responseData.message || 'Failed to delete payment.',
        });
        return;
      }

      toast({
        title: 'Success',
        description: 'Payment deleted successfully.',
      });

      await queryClient.invalidateQueries({ queryKey: ['supplier-payments', supplierId] });
      await queryClient.invalidateQueries({ queryKey: ['supplier-balance'] });
      handleCloseDeleteConfirm();
    } catch (err) {
      console.error('❌ Error deleting payment:', err);
      toast({
        variant: 'destructive',
        title: 'Delete Failed',
        description: err instanceof Error ? err.message : 'An unexpected error occurred while deleting the payment.',
      });
    } finally {
      setIsDeleting(false);
    }
  };

  const clearField = (fieldName: 'paidAmount' | 'reference') => {
    setPaymentForm((prev) => ({ ...prev, [fieldName]: '' }));
  };

  // Lazy loading: Load more items
  const loadMoreItems = useCallback(() => {
    if (isMobileLoading || mobileLoadedCount >= payments.length) return;
    
    setIsMobileLoading(true);
    const delay = isAndroid ? 300 : 200;
    
    setTimeout(() => {
      const newCount = Math.min(mobileLoadedCount + 10, payments.length);
      setMobileLoadedCount(newCount);
      setIsMobileLoading(false);
    }, delay);
  }, [isMobileLoading, mobileLoadedCount, payments.length, isAndroid]);

  // Scroll handler for lazy loading
  const handleMobileScroll = useCallback(() => {
    if (window.innerWidth >= 1024) return; // Desktop - no lazy loading
    if (isMobileLoading || mobileLoadedCount >= payments.length) return;

    const mobileContainer = mobileListRef.current;
    let scrollTop, scrollHeight, clientHeight;
    
    if (mobileContainer) {
      scrollTop = mobileContainer.scrollTop;
      scrollHeight = mobileContainer.scrollHeight;
      clientHeight = mobileContainer.clientHeight;
    } else {
      scrollTop = window.scrollY;
      scrollHeight = document.documentElement.scrollHeight;
      clientHeight = window.innerHeight;
    }
    
    // More aggressive loading threshold for Android
    const threshold = isAndroid ? 200 : 100;
    
    // Check if we're near the bottom
    if (scrollHeight - (scrollTop + clientHeight) < threshold) {
      loadMoreItems();
    }
  }, [isMobileLoading, mobileLoadedCount, payments.length, loadMoreItems, isAndroid]);

  // Throttled scroll handler
  const throttledScrollHandler = useCallback(() => {
    let ticking = false;
    
    return () => {
      if (!ticking) {
        requestAnimationFrame(() => {
          handleMobileScroll();
          ticking = false;
        });
        ticking = true;
      }
    };
  }, [handleMobileScroll]);

  // Set up scroll listener for mobile lazy loading
  useEffect(() => {
    if (window.innerWidth >= 1024) return; // Desktop - no lazy loading
    const mobileContainer = mobileListRef.current;
    const scrollHandler = throttledScrollHandler();

    if (mobileContainer) {
      mobileContainer.addEventListener('scroll', scrollHandler, { passive: true });
      return () => mobileContainer.removeEventListener('scroll', scrollHandler);
    } else {
      window.addEventListener('scroll', scrollHandler, { passive: true });
      return () => window.removeEventListener('scroll', scrollHandler);
    }
  }, [throttledScrollHandler]);

  // Reset lazy loading when payments change
  useEffect(() => {
    if (payments.length > 0) {
      const initialCount = isAndroid ? 30 : 20;
      setMobileLoadedCount(Math.min(initialCount, payments.length));
    }
  }, [payments.length, isAndroid]);

  return (
    <div className="flex flex-col space-y-4 p-4 lg:p-0">
      {/* Add Payment Button */}
      <div className="flex items-center justify-end gap-4">
        <Button
          variant="default"
          onClick={handleOpenAddPayment}
          className="flex items-center gap-2"
        >
          <Plus className="h-5 w-5" />
          <span className="hidden sm:inline">Add Payment</span>
        </Button>
      </div>

      {/* Content Area */}
      <div className="flex-1 flex flex-col overflow-hidden">
        {/* Desktop Table View */}
          <div className="hidden lg:block overflow-auto rounded-lg border shadow-sm">
            {isLoading ? (
              <div className="flex items-center justify-center h-64">
                <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
              </div>
            ) : error ? (
              <div className="flex items-center justify-center h-64 text-red-600">
                Error loading payments
              </div>
            ) : payments.length === 0 ? (
              <div className="flex items-center justify-center h-64 text-gray-500">
                No payments found
              </div>
            ) : (
              <Table>
                <TableHeader className="bg-gray-50">
                  <TableRow className="hover:bg-gray-100">
                    <TableHead className="font-semibold">Paid Amount</TableHead>
                    <TableHead className="font-semibold">Reference</TableHead>
                    <TableHead className="font-semibold">Paid On</TableHead>
                    <TableHead className="font-semibold">Payment Type</TableHead>
                    <TableHead className="text-center font-semibold">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {payments.map((payment) => (
                    <TableRow key={payment.ID} className="hover:bg-gray-50 transition-colors">
                      <TableCell className="font-semibold text-blue-600">
                        {formatCurrency(payment.PaidAmount)}
                      </TableCell>
                      <TableCell className="text-gray-700">{payment.Reference || '-'}</TableCell>
                      <TableCell className="text-gray-700">{formatDateTime(payment.PaidOn)}</TableCell>
                      <TableCell>
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                          {payment.PaymentTypeText}
                        </span>
                      </TableCell>
                      <TableCell className="text-center">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleOpenDeleteConfirm(payment.ID)}
                          className="text-red-600 hover:text-red-700 hover:bg-red-50"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </div>

          {/* Mobile Card View */}
          <div 
            ref={mobileListRef}
            className="block lg:hidden overflow-y-auto"
            style={{ maxHeight: 'calc(100vh - 200px)' }}
          >
            {isLoading ? (
              <div className="flex items-center justify-center py-12">
                <div className="flex flex-col items-center gap-3">
                  <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
                  <span className="text-sm text-gray-500">Loading payments...</span>
                </div>
              </div>
            ) : error ? (
              <div className="text-center py-12 text-red-600">Error loading payments</div>
            ) : payments.length === 0 ? (
              <div className="text-center py-12 text-gray-500">No payments found</div>
            ) : (
              <div className="space-y-4 px-4 pb-4">
                {/* Render only loaded cards */}
                {payments.slice(0, mobileLoadedCount).map((payment) => (
                  <Card
                    key={payment.ID}
                    className="bg-white rounded-lg shadow-md border hover:shadow-lg transition-shadow duration-200"
                  >
                    <div className="p-4 space-y-3">
                      <div className="flex justify-between items-start">
                        <div className="font-bold text-lg text-blue-600">
                          {formatCurrency(payment.PaidAmount)}
                        </div>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleOpenDeleteConfirm(payment.ID)}
                          className="text-red-600 hover:text-red-700 hover:bg-red-50 -mt-2"
                        >
                          <Trash2 className="h-5 w-5" />
                        </Button>
                      </div>

                      <div className="space-y-2 text-sm">
                        <div className="flex justify-between items-center py-1.5 border-b border-gray-100">
                          <span className="text-gray-600 font-medium">Reference:</span>
                          <span className="text-gray-900">{payment.Reference || '-'}</span>
                        </div>

                        <div className="flex justify-between items-center py-1.5 border-b border-gray-100">
                          <span className="text-gray-600 font-medium">Paid On:</span>
                          <span className="text-gray-900">{formatDateTime(payment.PaidOn)}</span>
                        </div>

                        <div className="flex justify-between items-center py-1.5">
                          <span className="text-gray-600 font-medium">Payment Type:</span>
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                            {payment.PaymentTypeText}
                          </span>
                        </div>
                      </div>
                    </div>
                  </Card>
                ))}

                {/* Loading indicator */}
                {isMobileLoading && (
                  <div className="flex items-center justify-center py-4">
                    <Loader2 className="h-6 w-6 animate-spin text-blue-600" />
                    <span className="ml-2 text-sm text-gray-500">Loading more payments...</span>
                  </div>
                )}

                {/* End of list indicator */}
                {mobileLoadedCount >= payments.length && payments.length > 0 && (
                  <div className="text-center py-4 text-sm text-gray-500">
                    All payments loaded ({payments.length} total)
                  </div>
                )}

                {/* Load more trigger (invisible element at bottom) */}
                {mobileLoadedCount < payments.length && (
                  <div className="h-1" aria-hidden="true" />
                )}
              </div>
          )}
        </div>
      </div>

      {/* Add Payment Dialog */}
      <Dialog
        open={isAddPaymentOpen}
        onOpenChange={(open) => {
          if (!open) {
            handleCloseAddPayment();
          } else {
            setIsAddPaymentOpen(true);
          }
        }}
      >
        <DialogContent className="max-w-md w-[95vw] sm:w-full max-h-[90vh] overflow-hidden flex flex-col rounded-2xl backdrop-blur-sm bg-white/95 animate-in fade-in-0 zoom-in-95">
          <DialogHeader className="flex-shrink-0">
            <DialogTitle className="text-2xl font-bold text-gray-900">Add Payment</DialogTitle>
            <DialogDescription className="text-gray-600">
              Enter payment details for the supplier. Fields marked with * are required.
            </DialogDescription>
          </DialogHeader>

          <div ref={dialogContentRef} className="grid grid-cols-1 gap-5 py-4 overflow-y-auto flex-1 min-h-0 pr-2 -mr-2">
            {/* Invoice - hidden per requirement */}

            {/* Company */}
            <div className="flex flex-col gap-2">
              <Label className="flex items-center gap-1 font-semibold">
                Company <span className="text-red-500">*</span>
              </Label>
              <Select
                value={paymentForm.companyId}
                onValueChange={(value) => setPaymentForm((prev) => ({ ...prev, companyId: value }))}
                disabled={isCompaniesLoading || companies.length === 0}
                required
              >
                <SelectTrigger className="border-gray-300 focus:ring-2 focus:ring-blue-500">
                  <SelectValue placeholder={isCompaniesLoading ? 'Loading companies...' : 'Select company'} />
                </SelectTrigger>
                <SelectContent>
                  {companies.map((company: any) => (
                    <SelectItem 
                      key={company.Id || company.CompanyID || company.id} 
                      value={String(company.Id || company.CompanyID || company.id)}
                    >
                      {company.Name || company.CompanyName || company.name || `Company ${company.Id || company.CompanyID}`}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {!isCompaniesLoading && companies.length === 0 && (
                <p className="text-sm text-red-500">No companies available.</p>
              )}
            </div>

            {/* Location */}
            <div className="flex flex-col gap-2">
              <Label className="flex items-center gap-1 font-semibold">
                Location <span className="text-red-500">*</span>
              </Label>
              <Select
                value={paymentForm.locationId}
                onValueChange={(value) => setPaymentForm((prev) => ({ ...prev, locationId: value }))}
                disabled={isLocationsLoading || locations.length === 0}
                required
              >
                <SelectTrigger className="border-gray-300 focus:ring-2 focus:ring-blue-500">
                  <SelectValue placeholder={isLocationsLoading ? 'Loading locations...' : 'Select location'} />
                </SelectTrigger>
                <SelectContent>
                  {locations.map((location: any) => (
                    <SelectItem 
                      key={location.ID || location.LocationID || location.LocationId} 
                      value={String(location.ID || location.LocationID || location.LocationId)}
                    >
                      {location.Name || location.LocationName || location.name || `Location ${location.ID || location.LocationID}`}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {!isLocationsLoading && locations.length === 0 && (
                <p className="text-sm text-red-500">No locations available.</p>
              )}
            </div>

            {/* Link Invoice */}
            <div className="flex flex-col gap-2">
              <Label className="text-sm font-medium text-gray-700 flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={paymentForm.linkInvoice}
                  onChange={async (e) => {
                    const checked = e.target.checked;
                    setPaymentForm((prev) => ({
                      ...prev,
                      linkInvoice: checked,
                      invoiceSearch: '',
                      selectedInvoiceId: '',
                      selectedInvoiceCompanyId: '',
                    }));
                    
                    if (checked) {
                      await fetchSupplierDueInvoices(String(supplierId));
                    } else {
                      setInvoiceOptions([]);
                    }
                  }}
                  className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                />
                Link to Invoice
              </Label>
              
              {paymentForm.linkInvoice && (
                <div className="relative">
                  <Input
                    placeholder="Search invoice"
                    value={paymentForm.invoiceSearch}
                    list="invoice-options"
                    onChange={(e) => {
                      const value = e.target.value;
                      setPaymentForm((prev) => ({
                        ...prev,
                        invoiceSearch: value,
                      }));

                      // Auto-select if exact match
                      const match = invoiceOptions.find(
                        (opt) => opt.InvRefNo.toLowerCase() === value.toLowerCase()
                      );
                      if (match) {
                        setPaymentForm((prev) => ({
                          ...prev,
                          selectedInvoiceId: match.ID,
                          selectedInvoiceCompanyId: match.companyid ? String(match.companyid) : '',
                        }));
                      }
                    }}
                    className="w-full"
                  />
                  <datalist id="invoice-options">
                    {invoiceOptions.map((inv) => (
                      <option key={inv.ID} value={inv.InvRefNo}>
                        {inv.InvRefNo}
                      </option>
                    ))}
                  </datalist>
                  {invoiceLoading && (
                    <div className="absolute right-3 top-1/2 -translate-y-1/2">
                      <Loader2 className="h-4 w-4 animate-spin text-gray-400" />
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Paid Amount */}
            <div className="flex flex-col gap-2">
              <Label htmlFor="paid-amount" className="flex items-center gap-1 font-semibold">
                Paid Amount <span className="text-red-500">*</span>
              </Label>
              <div className="relative">
                <Input
                  id="paid-amount"
                  type="number"
                  placeholder="0.00"
                  value={paymentForm.paidAmount}
                  onChange={(e) => setPaymentForm((prev) => ({ ...prev, paidAmount: e.target.value }))}
                  required
                  className="border-gray-300 pr-10 focus:ring-2 focus:ring-blue-500"
                />
                {paymentForm.paidAmount && (
                  <button
                    type="button"
                    onClick={() => clearField('paidAmount')}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors"
                    aria-label="Clear paid amount"
                  >
                    <X className="h-4 w-4" />
                  </button>
                )}
              </div>
            </div>

            {/* Reference */}
            <div className="flex flex-col gap-2">
              <Label htmlFor="reference" className="font-semibold">
                Reference
              </Label>
              <div className="relative">
                <Input
                  id="reference"
                  placeholder="Payment reference or note"
                  value={paymentForm.reference}
                  onChange={(e) => setPaymentForm((prev) => ({ ...prev, reference: e.target.value }))}
                  className="border-gray-300 pr-10 focus:ring-2 focus:ring-blue-500"
                />
                {paymentForm.reference && (
                  <button
                    type="button"
                    onClick={() => clearField('reference')}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors"
                    aria-label="Clear reference"
                  >
                    <X className="h-4 w-4" />
                  </button>
                )}
              </div>
            </div>

            {/* Paid On */}
            <div className="flex flex-col gap-2">
              <Label htmlFor="paid-on" className="flex items-center gap-1 font-semibold">
                Paid On <span className="text-red-500">*</span>
              </Label>
              <Input
                id="paid-on"
                type="date"
                value={paymentForm.paidOn}
                onChange={(e) => setPaymentForm((prev) => ({ ...prev, paidOn: e.target.value }))}
                required
                className="border-gray-300 focus:ring-2 focus:ring-blue-500"
              />
            </div>

            {/* Payment Type - Radio Buttons */}
            <div className="flex flex-col gap-3">
              <Label className="flex items-center gap-1 font-semibold">
                Payment Type <span className="text-red-500">*</span>
              </Label>
              <div className="grid grid-cols-2 gap-3">
                {/* Cash */}
                <div className="flex items-center space-x-2">
                  <input
                    type="radio"
                    id="payment-cash"
                    name="paymentType"
                    value="1"
                    checked={paymentForm.paymentType === '1'}
                    onChange={(e) => setPaymentForm((prev) => ({ ...prev, paymentType: e.target.value, bankId: '', bankAccountNo: '' }))}
                    className="h-4 w-4 cursor-pointer text-blue-600 focus:ring-2 focus:ring-blue-500"
                  />
                  <Label htmlFor="payment-cash" className="font-normal cursor-pointer">
                    Cash
                  </Label>
                </div>

                {/* Card */}
                <div className="flex items-center space-x-2">
                  <input
                    type="radio"
                    id="payment-card"
                    name="paymentType"
                    value="3"
                    checked={paymentForm.paymentType === '3'}
                    onChange={(e) => setPaymentForm((prev) => ({ ...prev, paymentType: e.target.value, bankId: '', bankAccountNo: '' }))}
                    className="h-4 w-4 cursor-pointer text-blue-600 focus:ring-2 focus:ring-blue-500"
                  />
                  <Label htmlFor="payment-card" className="font-normal cursor-pointer">
                    Card
                  </Label>
                </div>

                {/* Wallet */}
                <div className="flex items-center space-x-2">
                  <input
                    type="radio"
                    id="payment-wallet"
                    name="paymentType"
                    value="7"
                    checked={paymentForm.paymentType === '7'}
                    onChange={(e) => setPaymentForm((prev) => ({ ...prev, paymentType: e.target.value, bankId: '', bankAccountNo: '' }))}
                    className="h-4 w-4 cursor-pointer text-blue-600 focus:ring-2 focus:ring-blue-500"
                  />
                  <Label htmlFor="payment-wallet" className="font-normal cursor-pointer">
                    Wallet
                  </Label>
                </div>

                {/* Cheque */}
                <div className="flex items-center space-x-2">
                  <input
                    type="radio"
                    id="payment-cheque"
                    name="paymentType"
                    value="2"
                    checked={paymentForm.paymentType === '2'}
                    onChange={(e) => setPaymentForm((prev) => ({ ...prev, paymentType: e.target.value, bankId: '', bankAccountNo: '' }))}
                    className="h-4 w-4 cursor-pointer text-blue-600 focus:ring-2 focus:ring-blue-500"
                  />
                  <Label htmlFor="payment-cheque" className="font-normal cursor-pointer">
                    Cheque
                  </Label>
                </div>

              </div>
            </div>

            {/* Bank Fields - Show only when Bank Transfer is selected */}
            {paymentForm.paymentType === '5' && (
              <div ref={bankFieldsRef} className="flex flex-col gap-4 p-4 bg-blue-50 rounded-lg border border-blue-200">
                {/* Bank Name */}
                <div className="flex flex-col gap-2">
                  <Label className="flex items-center gap-1 font-semibold text-blue-900">
                    Bank Name <span className="text-red-500">*</span>
                  </Label>
                  <Select
                    value={paymentForm.bankId}
                    onValueChange={handleBankChange}
                    disabled={isBanksLoading || banks.length === 0}
                    required
                  >
                    <SelectTrigger className="border-blue-300 focus:ring-2 focus:ring-blue-500 bg-white">
                      <SelectValue placeholder={isBanksLoading ? 'Loading banks...' : 'Select bank'} />
                    </SelectTrigger>
                    <SelectContent>
                      {banks.map((bank: any) => (
                        <SelectItem 
                          key={bank.Id} 
                          value={String(bank.Id)}
                        >
                          {bank.BankName}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {!isBanksLoading && banks.length === 0 && (
                    <p className="text-sm text-red-500">No banks available.</p>
                  )}
                </div>

                {/* A/c No */}
                <div className="flex flex-col gap-2">
                  <Label className="flex items-center gap-1 font-semibold text-blue-900">
                    A/c No <span className="text-red-500">*</span>
                  </Label>
                  <Select
                    value={paymentForm.bankAccountNo}
                    onValueChange={handleAccountNoChange}
                    disabled={isBanksLoading || banks.length === 0}
                    required
                  >
                    <SelectTrigger className="border-blue-300 focus:ring-2 focus:ring-blue-500 bg-white">
                      <SelectValue placeholder={isBanksLoading ? 'Loading accounts...' : 'Select account'} />
                    </SelectTrigger>
                    <SelectContent>
                      {banks.map((bank: any) => (
                        <SelectItem 
                          key={`${bank.Id}-${bank.AccountNo}`} 
                          value={bank.AccountNo}
                        >
                          {bank.AccountNo}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {!isBanksLoading && banks.length === 0 && (
                    <p className="text-sm text-red-500">No accounts available.</p>
                  )}
                </div>
              </div>
            )}
          </div>

          <DialogFooter className="flex flex-col-reverse sm:flex-row sm:justify-end gap-3 pt-2 flex-shrink-0">
            <Button
              variant="outline"
              onClick={handleCloseAddPayment}
              disabled={isSubmitting}
              className="border-gray-300"
            >
              Cancel
            </Button>
            <Button
              onClick={handleSavePayment}
              disabled={isSubmitting}
              className="bg-blue-600 hover:bg-blue-700 text-white"
            >
              {isSubmitting ? (
                <span className="flex items-center gap-2">
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Saving...
                </span>
              ) : (
                'Save'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={isDeleteConfirmOpen}
        onOpenChange={(open) => {
          if (!open) {
            handleCloseDeleteConfirm();
          } else {
            setIsDeleteConfirmOpen(true);
          }
        }}
      >
        <DialogContent className="max-w-md w-[95vw] sm:w-full rounded-2xl backdrop-blur-sm bg-white/95 animate-in fade-in-0 zoom-in-95">
          <DialogHeader>
            <DialogTitle className="text-xl font-semibold text-red-600">Delete Payment</DialogTitle>
            <DialogDescription className="text-gray-600">
              Are you sure you want to delete this payment? This action cannot be undone.
            </DialogDescription>
          </DialogHeader>

          <DialogFooter className="flex flex-col-reverse sm:flex-row sm:justify-end gap-3 pt-4">
            <Button variant="outline" onClick={handleCloseDeleteConfirm} disabled={isDeleting} className="border-gray-300">
              Cancel
            </Button>
            <Button variant="destructive" onClick={handleDeletePayment} disabled={isDeleting}>
              {isDeleting ? (
                <span className="flex items-center gap-2">
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Deleting...
                </span>
              ) : (
                <>
                  <Trash2 className="h-4 w-4 mr-1" />
                  Delete
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default SupplierPayments;

